import { Component } from '@theme/component';

/**
 * @typedef {object} BroskeCatalogRefs
 * @property {HTMLButtonElement[]} [tabs] - Tab buttons (absent when there is a single tab).
 * @property {HTMLElement[]} panels - One panel per tab.
 */

/**
 * Tabs + product carousels for `sections/broske-catalog.liquid`.
 *
 * @extends {Component<BroskeCatalogRefs>}
 */
class BroskeCatalogComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Scroll does not bubble, so listen in the capture phase for every track inside.
    this.addEventListener('scroll', this.#onTrackScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#updateAllArrows, { passive: true, signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.#updateAllArrows();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateAllArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLElement)) return;

    this.#activate(Number(tab.dataset.index));
  }

  /**
   * Arrow keys / Home / End move between tabs (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    if (!tabs.length) return;

    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    /** @type {number | undefined} */
    let next;
    if (event.key === forward) next = (current + 1) % tabs.length;
    else if (event.key === backward) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;

    if (next === undefined) return;

    event.preventDefault();
    this.#activate(next);
    tabs[next]?.focus();
  }

  /** @param {MouseEvent} event */
  scrollNext(event) {
    this.#scrollBy(event, 1);
  }

  /** @param {MouseEvent} event */
  scrollPrev(event) {
    this.#scrollBy(event, -1);
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    if (!panels[index]) return;

    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    const track = panels[index].querySelector('[data-bk-track]');
    if (track instanceof HTMLElement) this.#updateArrows(track);
  }

  /**
   * @param {MouseEvent} event
   * @param {1 | -1} direction
   */
  #scrollBy(event, direction) {
    const button = event.target instanceof Element ? event.target : null;
    const track = button?.closest('.bk-catalog__products')?.querySelector('[data-bk-track]');
    if (!(track instanceof HTMLElement)) return;

    const slide = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: step * direction * (isRtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  /** @param {Event} event */
  #onTrackScroll = (event) => {
    const track = event.target;
    if (track instanceof HTMLElement && track.hasAttribute('data-bk-track')) this.#updateArrows(track);
  };

  #updateAllArrows = () => {
    for (const track of this.querySelectorAll('[data-bk-track]')) {
      if (track instanceof HTMLElement) this.#updateArrows(track);
    }
  };

  /**
   * Hide the arrow that cannot scroll any further.
   * @param {HTMLElement} track
   */
  #updateArrows(track) {
    const container = track.closest('.bk-catalog__products');
    const prev = container?.querySelector('.bk-catalog__arrow--prev');
    const next = container?.querySelector('.bk-catalog__arrow--next');
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth;

    if (prev instanceof HTMLButtonElement) prev.disabled = position <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = position >= max - 2;
  }

  /**
   * In the theme editor, selecting a tab block opens that tab.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    const index = Number(target.dataset.index);
    if (!Number.isNaN(index)) this.#activate(index);
  };
}

if (!customElements.get('broske-catalog-component')) {
  customElements.define('broske-catalog-component', BroskeCatalogComponent);
}
